import { css } from "@linaria/core";
import classNames from "classnames";
import React, { memo, useEffect, useMemo } from "react";
import { Outlet } from "react-router";
import { useFaultEvents, type FaultEvent } from "../../../../../../api/useFaultEvents";
import { type Project } from "../../../../../../api/useProjects";
import { useProjectSource } from "../../../../../../api/useSources";
import { colors } from "@ui/Theme";
import { useCanUserDoOnProject } from "../../../../../canUserDo";
import { useEventPeriod } from "../../../hooks/useEventPeriod";
import Activity from "./components/SidePanel/Activity";
import Details from "./components/SidePanel/Details";
import Parameters from "./components/SidePanel/Parameters";
import SuspenseSidePanel from "../components/SidePanel/SuspenseSidePanel";
import Header from "../components/Header";
import TitleHeader from "../components/Header/TitleHeader";
import { getFaultEventPriority } from "../../../utils/faultEvent";
import { isMultiFaultEvent } from "../../../../../../domain/event";
import { canEventBeUngrouped } from "../../../../../../domain/event/grouping";
import { GitBranchLine, GitMergeLine, LinkUnlinkM } from "@ui/IconV2/react";
import t from "../../../../../../i18n";
import Modal from "@ui/Modal";
import { useModals } from "@ui/lib/hooks/useModal";
import ModalFormSplitEvent from "../../../components/TransactionalModals/ModalFormSplitEvent";
import ModalFormMergeEvent from "../../../components/TransactionalModals/ModalFormMergeEvent";
import ModalFormUngroupEvent from "../../../components/TransactionalModals/ModalFormUngroupEvent";
import useSiblingEvents from "../../../hooks/useSiblingEvents";
import { notNull } from "diagnostics-lib/typescript";
import Option from "@ui/Option";
import { color } from "@ui";
import { getFaultEventDetectionText } from "../../../utils/faultEventDetection";
import { Tag } from "@ui/Tag";
import type { EventDiagnosed } from "../../../../../../api/useEvents";
import useActiveAnalysesStream from "../../../../../../api/useActiveAnalysesStream";
import { EventNavigation } from "../components/Header/EventNavigation";
import ResumeCard from "../components/Header/EventCardHeader/ResumeCard";
import LastCommentCard from "../components/Header/EventCardHeader/LastCommentCard";
import ActionIdentidiedCard from "../components/Header/EventCardHeader/ActionIdentifiedCard";
import { useSidePanelSelection } from "../../../hooks/useSidePanelSelection";
import {
  type SelectedSidePanelFaultEvent,
  useSelectedSidePanelFaultEvent,
} from "../../../hooks/createGlobalStates/useSelectedSidePanelFaultEvent";
import { useEventHistories } from "../../../../../../api/events/histories";
import { canSplitTopLevelEvent, listEventSourceIds } from "diagnostics-domain/faultEvent";
import { useSettings } from "../../../../../../api/useSettings";

const EXTEND_CONTENT = "extendContent";
const SPLIT_FAULT_EVENT_MODAL = "split-fault-event" as const;
const MERGE_FAULT_EVENT_MODAL = "merge-fault-event" as const;
const UNGROUP_FAULT_EVENT_MODAL = "ungroup-fault-event" as const;

export interface FaultEventOutletContext {
  event: EventDiagnosed;
  project: Project;
  onCloseEvent?: () => void;
}

const FaultEvent = ({ project, event }: { project: Project; event: EventDiagnosed }) => {
  const canUserEditFaultEvent = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const settings = useSettings();
  const { modal, closeModal, openModal } = useModals<string>();
  const { activeAnalyses } = useActiveAnalysesStream(project.id);
  const faultEventsHistory = useEventHistories({
    projectId: project.id,
    faultEventId: event.id,
    failureNames: event.metadata.faults.map(fault => fault.name),
  });

  const faultEventHistoryCount = useMemo(
    () => faultEventsHistory.reduce((acc, { events }) => acc + events.length, 0),
    [faultEventsHistory],
  );

  const source = useProjectSource(project);
  if (source === null) throw new Error("Source is undefined");
  useEventPeriod({
    event,
    source,
    project,
    analyses: activeAnalyses,
    boundaries: "active-analyses",
  });
  const siblingEvent = useSiblingEvents(project.id, event.id);
  const siblingEvents = useMemo(
    () => [siblingEvent.previous, siblingEvent.next].filter(notNull),
    [siblingEvent],
  );
  const faultEvents = useFaultEvents(event.projectId);
  const associatedEventIds = useMemo(
    () => listEventSourceIds(event, faultEvents.persistedEvents),
    [event, faultEvents.persistedEvents],
  );
  const canSplitEvent = useMemo(
    () => canSplitTopLevelEvent(event, { events: faultEvents.persistedEvents }),
    [event, faultEvents.persistedEvents],
  );
  const canUngroupEvent =
    canUserEditFaultEvent && settings.FAULT_EVENTS_GROUPING_ENABLED && canEventBeUngrouped(event);

  const {
    isFolded,
    setFolded,
    selectedSidePanel: selectedSidePanelFaultEvent,
    setSelectedSidePanel: setSelectedSidePanelFaultEvent,
    onClickSidePanel,
  } = useSidePanelSelection<SelectedSidePanelFaultEvent>({
    scope: project.id,
    useSelectionState: useSelectedSidePanelFaultEvent,
  });

  useEffect(() => {
    setFolded(true);
  }, [event.id, setFolded]);

  useEffect(() => {
    if (!canUserEditFaultEvent) {
      if (selectedSidePanelFaultEvent.type === "faultEventDetails") {
        setSelectedSidePanelFaultEvent({
          type: "details",
          tab: "fault",
        });
        setFolded(true);
      }
    }
  }, [
    selectedSidePanelFaultEvent,
    setSelectedSidePanelFaultEvent,
    canUserEditFaultEvent,
    setFolded,
  ]);

  const openCloseEventPanel = React.useCallback(() => {
    setSelectedSidePanelFaultEvent({
      type: "faultEventDetails",
      focusField: "status",
      initialStatus: "CLOSED",
    });
    setFolded(false);
  }, [setFolded, setSelectedSidePanelFaultEvent]);

  const detectionTag =
    event.metadata.isClosable && event.detection === false ? (
      <Tag className={tag} size="s">
        {getFaultEventDetectionText(false)}
      </Tag>
    ) : undefined;

  return (
    <>
      <div
        className={classNames(faultEvent, {
          isFolded,
        })}
      >
        <div className={classNames(faultEventContent)}>
          <Header
            titleHeader={
              <TitleHeader
                projectId={project.id}
                name={event.name}
                hasMultiFault={isMultiFaultEvent(event)}
                status={event.metadata.status}
                priority={getFaultEventPriority(event)}
                onClickComment={onClickSidePanel({
                  type: "comments",
                })}
                onClickActivity={onClickSidePanel({ type: "activity" })}
                isCommentActive={!isFolded && selectedSidePanelFaultEvent.type === "comments"}
                isActivityActive={!isFolded && selectedSidePanelFaultEvent.type === "activity"}
                reparability={event.reparability}
                optionsMenu={
                  <>
                    {canUngroupEvent && (
                      <Option onClick={() => openModal(UNGROUP_FAULT_EVENT_MODAL)} closeOnClick>
                        <LinkUnlinkM size="s" color={color.carbon800} />
                        <span>{t("project.fault_event.header.menu.option.ungroup")}</span>
                      </Option>
                    )}
                    <Option
                      title={
                        event.metadata.status === "CLOSED"
                          ? t("project.fault_event.header.menu.option.split.disabled.status.closed")
                          : siblingEvent.previous === null && siblingEvent.next === null
                            ? t("project.fault_event.header.menu.option.merge.disabled")
                            : undefined
                      }
                      onClick={() => openModal(MERGE_FAULT_EVENT_MODAL)}
                      disabled={
                        (siblingEvent.previous === null && siblingEvent.next === null) ||
                        event.metadata.status === "CLOSED"
                      }
                      closeOnClick
                    >
                      <GitMergeLine className={rotationIcon} size="s" color={color.carbon800} />
                      <span>{t("project.fault_event.header.menu.option.merge")}</span>
                    </Option>
                    <Option
                      title={
                        event.metadata.status === "CLOSED"
                          ? t("project.fault_event.header.menu.option.split.disabled.status.closed")
                          : canSplitEvent
                            ? undefined
                            : t("project.fault_event.header.menu.option.split.disabled")
                      }
                      onClick={() => openModal(SPLIT_FAULT_EVENT_MODAL)}
                      disabled={!canSplitEvent || event.metadata.status === "CLOSED"}
                      closeOnClick
                    >
                      <GitBranchLine className={rotationIcon} size="s" color={color.carbon800} />
                      <span>{t("project.fault_event.header.menu.option.split")}</span>
                    </Option>
                  </>
                }
                action={onClickSidePanel({
                  type: "faultEventDetails",
                })}
                tag={detectionTag}
              />
            }
            cards={{
              resumeCard: (
                <ResumeCard
                  duration={{
                    startsAt: event.startsAt,
                    endsAt: event.endsAt ?? null,
                    timeZone: project.timezone,
                  }}
                  faultNames={event.metadata.faults.map(fault => fault.name)}
                  historyCount={faultEventHistoryCount}
                  onClick={onClickSidePanel({
                    type: "details",
                    tab: "fault",
                  })}
                  onClickHistory={
                    faultEventHistoryCount > 0
                      ? onClickSidePanel({
                          type: "details",
                          tab: "history",
                        })
                      : undefined
                  }
                />
              ),
              lastCommentCard: (
                <LastCommentCard
                  projectId={project.id}
                  timezone={project.timezone}
                  event={event}
                  associatedEventIds={associatedEventIds}
                  onClick={onClickSidePanel({ type: "comments" })}
                />
              ),
              actionIdentifiedCard:
                (event.identifiedAction === true || event.metadata.status === "CLOSED") &&
                event.action !== "" &&
                event.action !== null ? (
                  <ActionIdentidiedCard
                    text={event.action}
                    onClick={
                      canUserEditFaultEvent
                        ? onClickSidePanel({
                            type: "faultEventDetails",
                            focusField: "actionIdentified",
                          })
                        : undefined
                    }
                  />
                ) : undefined,
            }}
            navigation={<EventNavigation />}
          />
          <Outlet
            context={
              {
                event,
                project,
                onCloseEvent: canUserEditFaultEvent ? openCloseEventPanel : undefined,
              } satisfies FaultEventOutletContext
            }
          />
        </div>

        {isFolded ? (
          <div />
        ) : selectedSidePanelFaultEvent.type === "details" ? (
          <SuspenseSidePanel>
            <Details
              isFolded={isFolded}
              project={project}
              faultEvent={event}
              closePanel={() => setFolded(s => !s)}
              tab={selectedSidePanelFaultEvent.tab}
              setTab={setSelectedSidePanelFaultEvent}
            />
          </SuspenseSidePanel>
        ) : selectedSidePanelFaultEvent.type === "activity" ? (
          <SuspenseSidePanel>
            <Activity
              isFolded={isFolded}
              project={project}
              faultEvent={event}
              associatedEventIds={associatedEventIds}
              mode="activity"
              closePanel={() => setFolded(true)}
            />
          </SuspenseSidePanel>
        ) : selectedSidePanelFaultEvent.type === "comments" ? (
          <SuspenseSidePanel>
            <Activity
              isFolded={isFolded}
              project={project}
              faultEvent={event}
              associatedEventIds={associatedEventIds}
              mode="comments"
              closePanel={() => setFolded(true)}
            />
          </SuspenseSidePanel>
        ) : (
          <SuspenseSidePanel>
            <Parameters
              projectId={project.id}
              isFolded={isFolded}
              faultEvent={event}
              closePanel={() => setFolded(s => !s)}
              focusField={selectedSidePanelFaultEvent.focusField}
              initialStatus={selectedSidePanelFaultEvent.initialStatus}
            />
          </SuspenseSidePanel>
        )}
      </div>
      <Modal
        title={t("project.fault_event.modal.split_event.title")}
        isOpen={modal === SPLIT_FAULT_EVENT_MODAL}
        onRequestClose={closeModal}
      >
        <ModalFormSplitEvent project={project} event={event} onRequestClose={closeModal} />
      </Modal>
      <Modal
        title={t("project.fault_event.modal.merge_event.title")}
        isOpen={modal === MERGE_FAULT_EVENT_MODAL}
        onRequestClose={closeModal}
      >
        <ModalFormMergeEvent
          project={project}
          event={event}
          siblingEvents={siblingEvents}
          onRequestClose={closeModal}
        />
      </Modal>
      <Modal
        title={t("project.fault_event.modal.ungroup_event.title")}
        isOpen={modal === UNGROUP_FAULT_EVENT_MODAL}
        onRequestClose={closeModal}
      >
        <ModalFormUngroupEvent project={project} event={event} onRequestClose={closeModal} />
      </Modal>
    </>
  );
};

export default memo(FaultEvent);

//#region CSS
// eslint-disable-next-line @typescript-eslint/no-unused-expressions
css`
  :global() {
    :root {
      --side-bar-width: 400px;
      --section-column-transition-duration: 350ms;
      --section-column-transition-ease: cubic-bezier(0.4, 0, 0.2, 1);
    }
  }
`;
const faultEvent = css`
  --right-column-width: var(--side-bar-width);
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) var(--right-column-width);
  grid-auto-rows: auto;
  flex-grow: 1;

  transition: grid-template-columns var(--section-column-transition-duration)
    var(--section-column-transition-ease);
  color: ${colors.carbon900};

  &.isFolded {
    --right-column-width: 0;
  }
`;
const faultEventContent = css`
  &.${EXTEND_CONTENT} {
    display: flex;
    flex-direction: column;
  }
`;
const rotationIcon = css`
  rotate: 90deg;
`;
const tag = css`
  flex-shrink: 0;
`;
//#endregion CSS
